import { Sparkle } from "@phosphor-icons/react";
import { getFoundingCityTheme } from "../lib/foundingCityTheme";

/**
 * FoundingCityBadge — "Philadelphia Founding Pro #1" style chip with
 * per-city color themes so each new market's founding cohort feels
 * visually distinct at a glance.
 *
 * Props:
 *   citySlug   — required. DB slug like "philadelphia" or "austin".
 *   slotNumber — optional. When present, renders "#N" after the label.
 *   grantedAt  — optional ISO date. Rendered into the `title=""` tooltip.
 *   size       — "sm" (compact, 10px text) or "md" (default, 11px text).
 *   className  — extra classes for layout tweaks at call sites.
 */
export default function FoundingCityBadge({
  citySlug,
  slotNumber,
  grantedAt,
  size = "md",
  className = "",
}) {
  if (!citySlug) return null;
  const theme = getFoundingCityTheme(citySlug);
  const cityLabel = theme.label
    || citySlug.charAt(0).toUpperCase() + citySlug.slice(1);

  const sizeClasses = size === "sm"
    ? "text-[10px] px-2 py-0.5 gap-1"
    : "text-[11px] px-3 py-1 gap-1.5";
  const iconSize = size === "sm" ? "h-2.5 w-2.5" : "h-3.5 w-3.5";

  const tooltip = grantedAt
    ? `Granted ${new Date(grantedAt).toLocaleDateString()} — permanent recognition for being one of ${cityLabel}'s first ${slotNumber ? slotNumber === 1 ? "founding" : `${slotNumber} founding` : "founding"} trucks on TruckTrackr.`
    : `${cityLabel} Founding Pro — permanent recognition for being one of the first trucks on TruckTrackr.`;

  return (
    <div
      className={`inline-flex items-center font-bold uppercase tracking-wider rounded-full shadow-sm bg-gradient-to-r ${theme.gradient} ${theme.darkGradient} ${theme.border} border ${theme.text} ${theme.darkText} ${sizeClasses} ${className}`}
      data-testid="profile-founding-badge"
      data-city-slug={citySlug}
      title={tooltip}
    >
      <Sparkle
        weight="fill"
        className={`${iconSize} ${theme.icon} ${theme.darkIcon}`}
      />
      <span>
        {cityLabel} Founding Pro{slotNumber ? ` #${slotNumber}` : ""}
      </span>
    </div>
  );
}
